<template lang="pug">
div.url-export-control
  md-menu.url-export-menu(md-size='4', md-align-trigger, :md-manual-toggle='isDraft', ref='urlMenu')
    md-button.md-icon-button.md-raised.btn(
      md-menu-trigger,
      :class='{ "copy-trigger-disabled": isDraft }',
      :title='"Copy video URL"',
      :aria-busy='isDraftProcessing ? "true" : "false"',
      @click='handleTriggerClick'
    )
      stitch-loader.copy-trigger-disabled__loader(v-if='isDraftProcessing', :loading='true', :size='20')
      md-icon.icon(:class='{ "copy-trigger-disabled__icon": isDraft }', v-else md-iconset="fa fa-link")
    md-menu-content
      md-subheader Copy URL for distribution
      div.dist-menu-loader(v-if='isMenuLoading')
        stitch-loader(:loading='true', :size='30')
      md-menu-item(v-else-if='menuDistributions && !menuDistributions.length')
        span No distributions found
      template(v-else-if='menuDistributions')
        md-menu-item.url-menu-item(
          v-for='dist in menuDistributions',
          :key='dist._id',
          :class='getMenuItemStateClass(dist)',
          @click='handleCopyClick(dist)'
        )
          div.dist-name {{ dist.name }}
          md-icon.dist-state-icon(
            v-if='shouldWarnDistribution(dist)'
            md-iconset="fa fa-exclamation-triangle"
          )
          md-tooltip(md-direction='top') {{ buildVideoUrl(dist) }}
</template>

<script>
import stitchLoader from '../Loader';
import distributionService from '../../services/distributionService';
import { buildVideoUrl } from '../../common/utils/videoUtils';

export default {
  components: { stitchLoader },
  props: {
    mediaItem: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    distributions: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      distributionDetailsById: {},
      processingDistributionId: null,
      draftDistributions: null,
      isDraftDistributionsLoading: false
    };
  },
  computed: {
    isDraft() {
      return this.mediaItem.status !== 'public';
    },
    isDraftProcessing() {
      return this.isDraftDistributionsLoading;
    },
    isMenuLoading() {
      return this.isDraft ? this.isDraftDistributionsLoading : this.isLoading;
    },
    menuDistributions() {
      if (!this.isDraft) {
        return this.distributions;
      }

      if (Array.isArray(this.distributions)) {
        return this.distributions;
      }

      return this.draftDistributions;
    }
  },
  methods: {
    // As we're using options API we need to "declare" this method here or Vue won't be able to find it
    buildVideoUrl(distribution) {
      return buildVideoUrl(this.mediaItem, distribution);
    },
    normalizeTag(tag) {
      if (!tag) {
        return '';
      }

      return String(tag).trim().toLowerCase();
    },
    getMediaItemTags() {
      return (this.mediaItem.tags || [])
        .map((tag) => this.normalizeTag(tag))
        .filter(Boolean);
    },
    getSectionTags(section) {
      return (section && section.tagsAssociated ? section.tagsAssociated : [])
        .map((tag) => this.normalizeTag(tag))
        .filter(Boolean);
    },
    getAvailableSections(distribution) {
      return Array.isArray(distribution && distribution.sections)
        ? distribution.sections.filter((section) => section && section.visible !== false)
        : [];
    },
    mediaItemBelongsToDistributionSection(distribution) {
      const mediaItemTags = this.getMediaItemTags();
      if (!mediaItemTags.length) {
        return false;
      }

      return this.getAvailableSections(distribution).some((section) => {
        const sectionTags = this.getSectionTags(section);
        return sectionTags.some((tag) => mediaItemTags.includes(tag));
      });
    },
    shouldWarnDistribution(distribution) {
      return this.isDraft || this.isKnownInvalidDistribution(distribution);
    },
    shouldHighlightDistribution(distribution) {
      return this.isKnownInvalidDistribution(distribution);
    },
    isKnownInvalidDistribution(distribution) {
      return Array.isArray(distribution && distribution.sections)
        ? !this.mediaItemBelongsToDistributionSection(distribution)
        : false;
    },
    getMenuItemStateClass(distribution) {
      return {
        'url-menu-item--guarded': this.shouldHighlightDistribution(distribution),
        'url-menu-item--warning': this.isDraft,
        'url-menu-item--processing': this.processingDistributionId === distribution._id
      };
    },
    extractDistributionResponse(response, fallbackDistribution) {
      const payload = response && response.data;
      if (!payload) {
        return fallbackDistribution;
      }

      if (payload.distribution && typeof payload.distribution === 'object') {
        return { ...fallbackDistribution, ...payload.distribution };
      }

      if (payload.distributions && !Array.isArray(payload.distributions) && typeof payload.distributions === 'object') {
        return { ...fallbackDistribution, ...payload.distributions };
      }

      if (payload._id) {
        return { ...fallbackDistribution, ...payload };
      }

      return fallbackDistribution;
    },
    extractDistributionList(response) {
      const payload = response && response.data;

      if (payload && Array.isArray(payload.distributions)) {
        return payload.distributions;
      }

      if (Array.isArray(payload)) {
        return payload;
      }

      return [];
    },
    isEligibleDistribution(distribution) {
      return Array.isArray(distribution && distribution.items)
        ? distribution.items.includes(this.mediaItem._id)
        : false;
    },
    async getResolvedDistribution(distribution) {
      if (Array.isArray(distribution && distribution.sections)) {
        return distribution;
      }

      if (this.distributionDetailsById[distribution._id]) {
        return this.distributionDetailsById[distribution._id];
      }

      const response = await distributionService.get(distribution._id);
      const resolvedDistribution = this.extractDistributionResponse(response, distribution);
      this.$set(this.distributionDetailsById, distribution._id, resolvedDistribution);
      return resolvedDistribution;
    },
    async copyTextToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      document.body.appendChild(textArea);
      textArea.select();

      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (!copied) {
        throw new Error('Copy command was rejected');
      }
    },
    showDraftAlert() {
      return this.$swal({
        title: 'Video is in Draft Mode',
        text: 'This video is currently a draft and is not yet public. We have copied the URL to your clipboard, but the link will not work until the video is live.',
        icon: 'warning',
        confirmButtonText: 'Understood',
        allowOutsideClick: false,
        allowEscapeKey: true
      });
    },
    showDraftWithoutDistributionAlert() {
      return this.$swal({
        title: 'Video is in Draft Mode',
        text: 'This video is currently a draft and is not yet public. We haven\'t copied the URL to your clipboard because it also does not belong to any distribution.',
        icon: 'warning',
        confirmButtonText: 'Understood',
        allowOutsideClick: false,
        allowEscapeKey: true
      });
    },
    showMissingSectionAlert() {
      return this.$swal({
        title: 'Video Setup Required',
        text: 'To play correctly, this video must be assigned to a section within your distribution. We have copied the URL to your clipboard, but the link will not work until the setup is complete.',
        icon: 'warning',
        confirmButtonText: 'Understood',
        allowOutsideClick: false,
        allowEscapeKey: true
      });
    },
    async getDraftDistributions() {
      if (Array.isArray(this.distributions)) {
        return this.distributions;
      }

      if (Array.isArray(this.draftDistributions)) {
        return this.draftDistributions;
      }

      this.isDraftDistributionsLoading = true;

      try {
        const response = await distributionService.getList({ offset: 0, limit: 1000 });
        const eligibleDistributions = this.extractDistributionList(response)
          .filter((distribution) => this.isEligibleDistribution(distribution));

        this.draftDistributions = eligibleDistributions;
        return eligibleDistributions;
      } finally {
        this.isDraftDistributionsLoading = false;
      }
    },
    handleTriggerClick() {
      if (this.isDraft) {
        this.handleDraftTriggerClick();
        return;
      }

      this.$emit('fetch');
    },
    closeDraftMenu() {
      if (this.isDraft && this.$refs.urlMenu && this.$refs.urlMenu.active) {
        this.$refs.urlMenu.close();
      }
    },
    async handleDraftTriggerClick() {
      if (this.processingDistributionId || this.isDraftDistributionsLoading) {
        return;
      }

      try {
        const draftDistributions = await this.getDraftDistributions();
        if (!draftDistributions.length) {
          await this.showDraftWithoutDistributionAlert();
          return;
        }

        await this.$nextTick();
        if (this.$refs.urlMenu) {
          this.$refs.urlMenu.open();
        }
      } catch (error) {
        console.error('Could not load draft video distributions', error);
        this.showCopyError();
      }
    },
    async handleCopyClick(distribution) {
      if (!distribution || this.processingDistributionId) {
        return;
      }

      this.closeDraftMenu();
      this.processingDistributionId = distribution._id;

      try {
        const resolvedDistribution = await this.getResolvedDistribution(distribution);
        await this.copyTextToClipboard(this.buildVideoUrl(resolvedDistribution));

        if (!this.mediaItemBelongsToDistributionSection(resolvedDistribution)) {
          await this.showMissingSectionAlert();
          return;
        }

        if (this.mediaItem.status !== 'public') {
          await this.showDraftAlert();
          return;
        }

        this.showCopySuccess();
      } catch (error) {
        console.error('Could not copy video URL', error);
        this.showCopyError();
      } finally {
        this.processingDistributionId = null;
      }
    },
    showCopySuccess() {
      this.$swal({
        text: 'URL copied to clipboard!',
        position: 'bottom-left',
        icon: 'success',
        toast: true,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
      });
    },

    showCopyError() {
      this.$swal({
        text: 'Could not copy URL',
        position: 'bottom-left',
        icon: 'error',
        toast: true,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.url-export-control,
.url-export-menu {
  display: inline-flex;
  align-items: center;
}

.copy-trigger-disabled {
  background-color: #f2f2f2;
  border-color: #c7c7c7;
  color: #7c7c7c;
  opacity: 0.9;
  box-shadow: none;
  cursor: pointer;
}

.copy-trigger-disabled:hover,
.copy-trigger-disabled:focus,
.copy-trigger-disabled:active {
  background-color: #f2f2f2;
  border-color: #c7c7c7;
  color: #7c7c7c;
  box-shadow: none;
}

.copy-trigger-disabled__icon {
  color: #9a9a9a;
  opacity: 0.8;
}

.copy-trigger-disabled__loader {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  transform: translateY(2px);
}

.dist-menu-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  min-width: 160px;
}

.dist-name {
  text-wrap: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: normal;
  font-size: 12px;
}

.url-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.url-menu-item--guarded {
  color: #8a5c00;
  background: rgba(255, 243, 205, 0.6);
  cursor: not-allowed;
}

.url-menu-item--warning {
  color: #8a5c00;
  background: rgba(255, 243, 205, 0.6);
}

.url-menu-item--processing {
  opacity: 0.6;
  pointer-events: none;
}

.dist-state-icon {
  font-size: 12px;
  margin-left: auto;
}
</style>

<style lang="scss">
.copy-trigger-disabled__loader.loader-wrapper {
  width: auto;
}

.copy-trigger-disabled__loader #loader {
  padding: 0;
}

.copy-trigger-disabled__loader .md-spinner-path {
  stroke: #ffffff;
}
</style>



// WEBPACK FOOTER //
// src/shared-components/mediaItem/CopyVideoUrlMenu.vue